import React,{useState,useEffect,useContext} from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { 
  Box, 
  Typography,
  Card,
  Grid,
  CircularProgress,
  Container,
} from '@mui/material';
import { DataGrid,GridToolbar } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfDay,endOfDay } from "date-fns";
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase'; 
import AppContext from '../AppContext';



const OrderReport = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({ totalOrders: [], orderItems: [] });
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const {shopId, t = (key) => key}=useContext(AppContext);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const ordersRef = collection(db, 'orders'); // Adjust the collection name
        const q = query(
          ordersRef,
          where('orderDate', '>=', startOfDay(fromDate)),
          where('orderDate', '<=', endOfDay(toDate)),
          where('shopId', '==', shopId),
        );
        const querySnapshot = await getDocs(q);

        const ordersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          orderDate: doc.data().orderDate.toDate(),
          ...doc.data(),
        }));
        setFilteredOrders(ordersData);
      } catch (error) {
        console.error('Error fetching orders: ', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [fromDate, toDate]);

  useEffect(() => {
    // Process data for charts
    const orderStatusCounts = filteredOrders.reduce((acc, order) => {
      const date = new Date(order.orderDate.toDate()).toLocaleDateString('en-GB');
      const status = order.orderStatus;
      if (!acc[date]) {
        acc[date] = { Completed: 0, Fulfilled: 0, Unfulfilled: 0 };
      }
      if (status === 'Completed' || status === 'Fulfilled' || status === 'Unfulfilled') {
        acc[date][status] += 1;
      }
      
      return acc;
    }, {});

    const totalOrdersLineChartData = Object.entries(orderStatusCounts).map(([date, counts]) => ({
      date,
      total:
        (counts['Completed'] || 0) +
        (counts['Fulfilled'] || 0) +
        (counts['Unfulfilled'] || 0),
    }));

    const orderItems = filteredOrders.reduce((acc, order) => {
      // Check if the orderStatus is 'Completed', 'Fulfilled', or 'Unfulfilled'
      if (['Completed', 'Fulfilled', 'Unfulfilled'].includes(order.orderStatus)) {
        order.cartItems.forEach((item) => {
          // Combine item name and variant to create a unique key
          const key = `${item.name} - ${item.selectedVariant}`;
        
          // Accumulate quantity based on the combined key
          acc[key] = (acc[key] || 0) + item.quantity;
        });
      }
      return acc;
    }, {});


    setChartData({
      totalOrders: totalOrdersLineChartData,
      orderItems: Object.entries(orderItems).map(([item, quantity]) => ({
        item,
        quantity,
      })),
    });
  }, [filteredOrders]); // <- Dependency on filteredOrders

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const itemColors = [
    '#1B5E20',
    '#0D47A1',
    '#4A148C',
    '#B71C1C',
    '#F57F17',
    '#006064',
    '#3E2723',
    '#263238',
    '#4E342E',
    '#2E7D32',
  ];
  // Prepare data for DataGrid
  const columns = [
    { field: 'orderID', headerName: t('Order ID'), width: 100 },
    { field: 'orderDate', headerName: t('Order Date'), width: 150 },
    { field: 'customerName', headerName: t('Customer Name'), width: 150 },
    { field: 'phoneNumber', headerName: t('Phone Number'), width: 150 },
    {
      field: 'cartItems',
      headerName: t('Item List'),
      width: 300,
    },
   // { field: 'total', headerName: 'Total Amount', width: 120 },
   // { field: 'paymentAmount', headerName: 'Paid Amount', width: 120 },
  //  { field: 'paymentType', headerName: 'Payment Type', width: 130 },
   // { field: 'remainingBalance', headerName: 'Remaining Balance', width: 160 },
    { field: 'userName', headerName: 'User', width: 150 },
   
  ];

  const rows = filteredOrders
  .filter(order => ['Completed', 'Fulfilled', 'Unfulfilled'].includes(order.orderStatus))
  .map((order) => ({
    id: order.id, // Ensure 'id' is unique for each row
    orderID: order.orderID,
    orderDate: new Date(order.orderDate.toDate()).toLocaleDateString('en-GB'),
    customerName: order.customerName,
    phoneNumber: order.phoneNumber,
    cartItems: order.cartItems.map((item) => `${item.name} x ${item.quantity}`).join(', '), // Convert to string
  //  total: formatter.format(order.total),
 //   paymentAmount: formatter.format(order.paymentAmount),
  //  paymentType: order.paymentType,
   // remainingBalance: formatter.format(order.remainingBalance),
    userName: order.userName,
  }));


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Container>
    <Typography variant="h4" gutterBottom>{t('Order Report')}</Typography>
    <Box mb={2} mt={3}>
          <Grid container spacing={2}>
            <Grid item>
              <DatePicker
                label={t('From Date')}
                value={fromDate}
                onChange={(newValue) => setFromDate(newValue)}
                format="dd/MM/yyyy"
              />
            </Grid>
            <Grid item>
              <DatePicker
                label={t('To Date')}
                value={toDate}
                onChange={(newValue) => setToDate(newValue)}
                format="dd/MM/yyyy"
              />
            </Grid>
          </Grid>
        </Box>
    {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={12} md={12}>
        <Card sx={{ height: 400 }} variant='outlined'>
          <Typography variant="h6" m={1}>{t('Total Orders')}</Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData.totalOrders} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value, name) => [formatter.format(value), name]} />
              <Legend />
              <Bar dataKey="total" name={t('Total Orders')} fill="#1976D2" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Grid>
     
    </Grid>
    
    <Grid container spacing={2} sx={{ mb: 4 }}>
      <Grid item xs={12} md={12}>
        <Card variant='outlined' sx={{height:'600px'}}>
          <Typography variant="h6" m={1}>{t('Order Items')}</Typography>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData.orderItems} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="item" interval={0} angle={-20} textAnchor="end" height={80} />
              <YAxis allowDecimals={false} />
              <Tooltip formatter={(value, name) => [formatter.format(value), name]} />
              <Legend />
              <Bar dataKey="quantity" name={t('Quantity')}>
                {chartData.orderItems.map((entry, index) => (
                  <Cell key={`cell-${entry.item}`} fill={itemColors[index % itemColors.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Grid>
     
    </Grid>
    
    <div style={{ height: '400px', width: '100%' }}>
    <Typography variant="h6" m={1}>{t('Order Details')}</Typography>
    <DataGrid rows={rows} columns={columns} pageSize={25}
        rowsPerPageOptions={[5,10,25]}
        slots={{ toolbar: GridToolbar }}
        loading={loading}
        disableSelectionOnClick />
    </div>
    </>
      )}
  </Container>
  </LocalizationProvider>
  );
};

export default OrderReport;
